import { Button, HStack, Text, useDisclosure } from "@chakra-ui/react";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import { useCallback } from "react";
import {
  RiDownload2Line,
  RiRestartLine,
  RiSettings3Line,
} from "react-icons/ri";
import sanitize from "sanitize-filename";
import { AudioFile, db, Phrase, Student } from "../db";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import SettingsDialog from "../dialogs/SettingsDialog";
import StudentConsentDialog from "../dialogs/StudentConsentDialog";
import { Settings } from "../Workbench";

// Object mapping sanitized phrases to original string.
type SanitizedPhrases = Record<string, string>;

interface HeaderProps {
  onRestart: () => void;
  settings: Settings;
  setSettings: (settings: Settings) => void;
  students: Student[];
  updateStudents: () => void;
}

const Header = ({
  onRestart,
  settings,
  setSettings,
  students,
  updateStudents,
}: HeaderProps) => {
  const handleDownloadData = useCallback(async () => {
    const zip = new JSZip();
    let audioFiles: AudioFile[] = [];
    let students: Student[] = [];
    let phrases: Phrase[] = [];
    try {
      audioFiles = await db.audioFiles.toArray();
      students = await db.students.toArray();
      phrases = await db.phrases.toArray();
    } catch (err) {
      console.error(`Failed to get files from database: ${err}`);
    }

    // Zip audio files.
    for (const f of audioFiles) {
      // Only download audio files for students with data collection consent.
      if (
        students.find((student) => student.id === f.studentId)
          ?.dataCollectionConsent
      ) {
        const phrase = sanitize(f.phrase);
        const filename = sanitize(`student-${f.studentId}-${f.phrase}.wav`);
        zip.file(`${phrase}/${filename}`, f.data);
      }
    }

    // Zip metadata.
    zip.file(
      "metadata.json",
      new Blob([JSON.stringify(settings)], {
        type: "application/json",
      })
    );

    // Zip phrases (original and sanitized versions).
    const sanitizedPhrases: SanitizedPhrases = {};
    for (const phrase of phrases) {
      sanitizedPhrases[sanitize(phrase.text)] = phrase.text;
    }
    zip.file(
      "phrases.json",
      new Blob([JSON.stringify(sanitizedPhrases)], {

        type: "application/json",
      })
    );

    try {
      const data = await zip.generateAsync({ type: "blob" });
      saveAs(data, "audio-data.zip");
    } catch (error: any) {
      console.error(error.name);
      console.error(error.message);
    }
  }, [settings]);

  const settingsDisclosure = useDisclosure();
  const restartDisclosure = useDisclosure();
  const studentConsentDisclosure = useDisclosure();

  return (
    <>
      <HStack
        justifyContent="space-between"
        bgColor="blue.600"
        py={2}
        px={5}
        w="full"
      >
        <Text as="h1" fontSize="3xl" fontWeight="semibold" color="white">
          Speech training data
        </Text>
        <HStack spacing={2}>
          <Button
            leftIcon={<RiRestartLine />}
            onClick={restartDisclosure.onOpen}
          >
            Start again
          </Button>
          <Button
            leftIcon={<RiDownload2Line />}
            onClick={studentConsentDisclosure.onOpen}
          >
            Download data
          </Button>
          <Button
            leftIcon={<RiSettings3Line />}
            onClick={settingsDisclosure.onOpen}
          >
            Settings
          </Button>
        </HStack>
      </HStack>
      {settingsDisclosure.isOpen && (
        <SettingsDialog
          isOpen={settingsDisclosure.isOpen}
          onClose={settingsDisclosure.onClose}
          settings={settings}
          setSettings={setSettings}
        />
      )}
      {restartDisclosure.isOpen && (
        <ConfirmDialog
          isOpen={restartDisclosure.isOpen}
          onClose={restartDisclosure.onClose}
          title="Start again"
          message='Starting again will delete all audio data collected and reset the students, phrases, and settings to their default values. Please click "confirm" to continue.'
          onConfirm={onRestart}
        />
      )}
      {studentConsentDisclosure.isOpen && (
        <StudentConsentDialog
          students={students}
          updateStudents={updateStudents}
          onDownloadData={handleDownloadData}
          isOpen={studentConsentDisclosure.isOpen}
          onClose={studentConsentDisclosure.onClose}
        />
      )}
    </>
  );
};

export default Header;
